import React, {useEffect, useState} from 'react';
import styles from '../styles/InstanceDetailsModal.module.css';
import axiosInstance from "../utility/axiosInstance";
import {Instance} from "../store/slices/instancesSlice";
import {useSelector} from 'react-redux';
import {RootState} from '../store/store';
import { formatRam } from '../utility/flavorOptions';

interface Props {
    open: boolean;
    onClose: () => void;
    instance: Instance | null;
}

interface Volume {
    id: string;
    name: string;
    status: string;
    size: number;
    bootable: string | boolean;
    created_at: any;
    device?: string;
    volume_type?: string;
}

interface ActionLogEntry {
    request_id: string;
    action: string;
    start_time: string;
}

const MobileInstanceDetailsModal: React.FC<Props> = ({open, onClose, instance}) => {
    const [activeTab, setActiveTab] = useState('details');
    const [volumes, setVolumes] = useState<Volume[]>([]);
    const [loading, setLoading] = useState(false);
    const [actionLogs, setActionLogs] = useState<ActionLogEntry[]>([]);
    const [loadingActionLogs, setLoadingActionLogs] = useState(false);
    const [sshPort, setSshPort] = useState<number | null>(null);
    const [loadingPort, setLoadingPort] = useState(false);

    const flavors = useSelector((state: RootState) => state.flavors.data);
    const images = useSelector((state: RootState) => state.images.data);

    const getFlavor = (id: string) => flavors.find(f => f.id === id);
    const getImage = (id: string) => images.find(img => img.id === id);

    const fetchActionLogs = async (instanceId: string) => {
        setLoadingActionLogs(true);
        try {
            const res = await axiosInstance.get(`/v1/instances/${instanceId}/actionlog`);
            setActionLogs(res.data || []);
        } catch (err) {
            console.error('Error fetching action logs:', err);
            setActionLogs([]);
        } finally {
            setLoadingActionLogs(false);
        }
    };

    const fetchVolumes = async (instanceId: string) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/instances/${instanceId}/volumes`);
            setVolumes(res.data || []);
        } catch (err) {
            console.error('Error fetching volumes:', err);
            setVolumes([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPort = async (floatingIp: any) => {
        setLoadingPort(true);
        try {
            const response = await axiosInstance.get('/v1/nginx/used-ports');
            const data = response.data;

            if (!data.routes || !Array.isArray(data.routes)) {
                console.error('Invalid response format:', data);
                return;
            }

            const route = data.routes.find((r: any) => r.targetIp === floatingIp && r.port);
            if (route) {
                setSshPort(route.port);
            }
        } catch (err) {
            console.error("Error fetching SSH port:", err);
        } finally {
            setLoadingPort(false);
        }
    };

    useEffect(() => {
        if (!instance?.id) return;

        if (activeTab === 'volume') {
            fetchVolumes(instance.id);
        } else if (activeTab === 'actionlog') {
            fetchActionLogs(instance.id);
        }
    }, [activeTab, instance?.id]);

    useEffect(() => {
        if (!instance) return;

        const floatingIp = Object.values(instance.addresses ?? {})
            .flat()
            .find((addr: any) => addr["OS-EXT-IPS:type"] === "floating")?.addr;

        if (floatingIp) {
            fetchPort(floatingIp);
        }
    }, [instance]);

    if (!open || !instance) return null;

    const flavor = getFlavor(instance.flavor_id);
    const image = getImage(instance.image_id);

    const host = instance.dns?.includes(":")
        ? instance.dns.split(":")[1]
        : instance.dns;

    const tabs = ['details', 'flavor', 'image', 'volume', 'actionlog'];

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} style={{maxHeight: '90vh', overflowY: 'auto'}}>
                <div className={styles.modalHeader}>
                    <h2>Instance: {instance.name}</h2>
                    <button onClick={onClose} className={styles.closeButton}>×</button>
                </div>

                <div className={styles.tabHeader}>
                    {tabs.map(tab => (
                        <button
                            key={tab}
                            className={activeTab === tab ? styles.activeTab : ''}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                <div className={styles.tabContent}>

                    {activeTab === 'details' && (
                        <div className={styles.section}>
                            <h4>Instance Details</h4>
                            <ul>
                                <li><strong>ID:</strong> {instance.id}</li>
                                <li><strong>Created At:</strong> {new Date(instance.created).toLocaleString()}</li>
                                <li>
                                    <strong>Network:</strong> {Object.keys(instance.addresses ?? {}).join(', ') || 'N/A'}
                                </li>
                                <li><strong>Image Name:</strong> {image?.name || instance.metadata?.imageName || 'N/A'}
                                </li>
                                <li><strong>Security Groups:</strong> {instance.security_groups?.join(', ') || 'N/A'}
                                </li>
                            </ul>
                        </div>
                    )}

                    {activeTab === 'flavor' && flavor && (
                        <div className={styles.section}>
                            <h4>Flavor Info</h4>
                            <ul>
                                <li><strong>Name:</strong> {flavor.name}</li>
                                <li><strong>vCPUs:</strong> {flavor.vcpus}</li>
                                <li><strong>RAM:</strong> {formatRam(flavor.ram)}</li>
                                <li><strong>Disk:</strong> {flavor.disk} GB</li>
                            </ul>
                        </div>
                    )}

                    {activeTab === 'image' && (
                        <div className={styles.section}>
                            <h4>Image Info</h4>
                            {image ? (
                                <ul>
                                    <li><strong>Name:</strong> {image.name}</li>
                                    <li><strong>ID:</strong> {image.id}</li>
                                </ul>
                            ) : (
                                <p>Image details not available (ID: {instance.image_id})</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'volume' && (
                        loading ? (
                            <p>Loading volumes...</p>
                        ) : volumes.length === 0 ? (
                            <p>No volumes attached.</p>
                        ) : (
                            <div>
                                {volumes.map((vol) => (
                                    <div key={vol.id} className={styles.section}>
                                        <h4>Volume: {vol.name || (String(vol.bootable) === "true" ? "boot-volume" : "N/A")}</h4>
                                        <ul>
                                            <li><strong>ID:</strong> {vol.id}</li>
                                            <li><strong>Status:</strong> {vol.status}</li>
                                            <li><strong>Size:</strong> {vol.size} GB</li>
                                            <li><strong>Device:</strong> {vol.device || 'N/A'}</li>
                                            <li><strong>Type:</strong> {vol.volume_type || 'N/A'}</li>
                                            <li><strong>Bootable:</strong> {String(vol.bootable)}</li>
                                            <li><strong>Created:</strong> {new Date(vol.created_at).toLocaleString()}
                                            </li>
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {activeTab === 'actionlog' && (
                        <div className="datatablenoactions">
                            {loadingActionLogs ? (
                                <p>Loading action log...</p>
                            ) : actionLogs.length === 0 ? (
                                <p>No actions recorded.</p>
                            ) : (
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>Action</th>
                                            <th>Time</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {actionLogs.map((action) => (
                                            <tr key={action.request_id}>
                                                <td>{action.action}</td>
                                                <td>{new Date(action.start_time).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MobileInstanceDetailsModal;